<template>
    <v-card>
        <v-card-title primary-title>{{ dialogTitle }}</v-card-title>
        <v-form ref="experimentGroupForm">
            <v-card-text>
                <v-row>
                    <v-col cols="6">
                        <v-text-field label="Name (*)" v-model="name" :rules="nameRequiredRule" />
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Platform"
                            v-model="platform"
                            :items="platformOptions"
                            :rules="platformRequiredRule"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Objective"
                            v-model="objective"
                            :items="objectiveOptions"
                            clearable
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Looker Explore Group"
                            v-model="lookerExploreGroupId"
                            :items="lookerExploreGroupOptions"
                            item-title="displayName"
                            item-value="id"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        Make available in:
                        <v-checkbox
                            class="mb-n8 pb-n8"
                            label="Experiment Setup"
                            v-model="isAvailableInExperimentSetup"
                        />
                        <v-checkbox
                            class="mb-n8 pb-n8"
                            label="Fermenter Run Setup"
                            v-model="isFermenterRunExperimentGroup"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Require Confluence Setup?"
                            v-model="isConfluenceExperimentSetupRequired"
                            :items="yesNoDropdownItems"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            label="Schema Config (*)"
                            v-model="selectedSchemaConfigOption"
                            :items="schemaConfigOptions"
                            return-object
                            v-if="schemaConfigOptions != null"
                            :rules="schemaConfigRequiredRule"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            label="Experiment Method"
                            v-model="selectedExperimentMethodOption"
                            :items="experimentMethodOptions"
                            return-object
                            v-if="experimentMethodOptions != null"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row v-if="userCanManageV2Metadata">
                    <v-col cols="12">
                        <v-card>
                            <v-card-title class="text-h6"
                                >Experiment Metadata Defaults</v-card-title
                            >
                            <v-card-text>
                                <p>
                                    Set the metadata values that will be defaulted for new
                                    experiments created under this group. Values can be overridden
                                    at the experiment level.
                                </p>
                                <experiment-metadata-template-fields
                                    metadata-context="experiment"
                                    v-model="experimentMetadata"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-form>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="primary" @click="saveExperimentGroup"> Save </v-btn>
        </v-card-actions>
    </v-card>
</template>

<style scoped>
.example {
    padding: 0;
}
</style>


<script setup lang="ts">
import { computed, ref } from "vue";
import { withLoadingScreen } from "@/lib/loading";
import {
    ExperimentGroupDTO,
    experimentGroupsAPI,
    ExperimentObjective,
    SavedExperimentGroup,
} from "@/services/experimentGroups/experimentGroupsAPI";
import { manageSchemasAPI, SavedSchemaConfigDTO } from "@/services/schemas/manageSchemasAPI";
import { experimentMethodsAPI, SavedExperimentMethod } from "@/services/experimentMethods/experimentMethodsAPI";
import { DropdownListItem, mapToDropdownListItemWithCodeAndName } from "@/lib/ts/dropdownListItemUtils";
import { requiredSimpleValueRules, requiredTextValueRules } from "@/lib/validationUtils";
import { EventBus } from "@/lib/event-bus";
import ExperimentMetadataTemplateFields from "@/components/Experiments/ExperimentMetadataTemplateFields.vue";
import { useAuth } from "@/lib/ts/auth";
import { PlatformDTO } from "@/services/commonModels";
import { experimentsAPI, mapPlatformsToDropdownItems } from "@/services/experiments/experimentsAPI";
import { RegistryEntry } from "@/services/registries/registriesAPI";

const auth = useAuth();

const userCanManageV2Metadata = computed(() => {
    return auth.isAuthorized("Experiments:ManageV2Metadata");
});

const { experimentGroup } = defineProps<{
    experimentGroup?: ExperimentGroupDTO
}>();

const emit = defineEmits<{
    (e: "cancelForm"): void;
    (e: "experimentGroupSaved", savedGroup: SavedExperimentGroup);
}>();

const name = ref(experimentGroup ? experimentGroup.name : "");
const objective = ref(experimentGroup ? experimentGroup.objective : ExperimentObjective.NONE);
const objectiveOptions = ref([
    "bioprocess development",
    "demo",
    "formulation testing",
    "method development",
    "salt stress testing",
    "stability testing",
    "screening",
]);
const platform = ref(experimentGroup ? experimentGroup.platform : null);
const platformOptions = ref<DropdownListItem<PlatformDTO>[]>([]);

const lookerExploreGroupId = ref<string | null>(experimentGroup?.lookerExploreGroupId ? experimentGroup.lookerExploreGroupId : null);
const lookerExploreGroupOptions = ref<RegistryEntry[]>([]);

const yesNoDropdownItems = [
    {
        title: "Yes",
        value: true
    },
    {
        title: "No",
        value: false
    }
]

const isAvailableInExperimentSetup = ref<boolean>(experimentGroup ? experimentGroup.isAvailableInExperimentSetup : true);
const isFermenterRunExperimentGroup = ref<boolean>(experimentGroup ? experimentGroup.isFermenterRunExperimentGroup : false);
const isConfluenceExperimentSetupRequired = ref<boolean>(experimentGroup ? experimentGroup.isConfluenceExperimentSetupRequired : false);
const selectedSchemaConfigOption = ref<DropdownListItem<SavedSchemaConfigDTO> | null>(experimentGroup ? mapToDropdownListItemWithCodeAndName(experimentGroup.schemaConfig) : null);
const selectedExperimentMethodOption = ref<DropdownListItem<SavedExperimentMethod> | null>(experimentGroup && experimentGroup.experimentMethod ? mapToDropdownListItemWithCodeAndName(experimentGroup.experimentMethod!) : null);
const schemaConfigOptions = ref<DropdownListItem<SavedSchemaConfigDTO>[]>();
const experimentMethodOptions = ref<DropdownListItem<SavedExperimentMethod>[]>([]);

const experimentGroupForm = ref();

const nameRequiredRule = requiredTextValueRules("Name");
const schemaConfigRequiredRule = requiredSimpleValueRules("Schema Config");
const platformRequiredRule = requiredSimpleValueRules("Platform");

const experimentMetadata = ref(
    experimentGroup && experimentGroup.experimentMetadata
        ? experimentGroup.experimentMetadata
        : ({} as Record<string, any>)
);

async function loadDropdownItems() {
    await withLoadingScreen(async () => {
        lookerExploreGroupOptions.value = await experimentGroupsAPI.fetchAllLookerExploreGroupOptions();

        const allSchemaConfigs = await manageSchemasAPI.fetchAllSchemaConfigs();
        schemaConfigOptions.value = allSchemaConfigs.map(mapToDropdownListItemWithCodeAndName);

        const allExperimentMethods = await experimentMethodsAPI.fetchAllExperimentMethods();
        experimentMethodOptions.value = allExperimentMethods.map(
            mapToDropdownListItemWithCodeAndName
        );

        const allPlatforms = await experimentsAPI.fetchPlatforms();
        platformOptions.value = mapPlatformsToDropdownItems(allPlatforms);
    });
}

const dialogTitle = computed(() => {
    return experimentGroup ? "Edit Experiment Group" : "Create Experiment Group";
});

function cancel() {
    emit("cancelForm");
}

async function saveExperimentGroup() {
    if (await validate()) {
        await withLoadingScreen(async () => {
            const schemaConfigOption: DropdownListItem<SavedSchemaConfigDTO> | null = selectedSchemaConfigOption.value;
            const schemaConfig: SavedSchemaConfigDTO | null = schemaConfigOption ? schemaConfigOption.value : null;

            const experimentMethodOption: DropdownListItem<SavedExperimentMethod> | null = selectedExperimentMethodOption.value;
            const experimentMethod: SavedExperimentMethod | null = experimentMethodOption ? experimentMethodOption.value : null;

            const group = {
                name: name.value,
                objective: objective.value || null,
                isAvailableInExperimentSetup: isAvailableInExperimentSetup.value,
                isFermenterRunExperimentGroup: isFermenterRunExperimentGroup.value,
                isConfluenceExperimentSetupRequired: isConfluenceExperimentSetupRequired.value,
                schemaConfigId: schemaConfig!.id,
                experimentMethodId: experimentMethod?.id || null,
                experimentMetadata: experimentMetadata.value || null,
                platformId: platform.value?.id || null,
                elnFolderId: experimentGroup?.elnFolderId || null,
                lookerExploreGroupId: lookerExploreGroupId.value || null,
            };

            const savedGroup = experimentGroup
                ? await experimentGroupsAPI.updateExperimentGroup({
                    ...group,
                    id: experimentGroup.id,
                    code: experimentGroup.code,
                })
                : await experimentGroupsAPI.createExperimentGroup(group);

            emit("experimentGroupSaved", savedGroup);
        });
    }
}

async function validate() {
    const validationResult = await experimentGroupForm.value.validate();
    if (!validationResult.valid) {
        const errors = validationResult.errors;
        const msgs = errors.map((error) => {
            return error.errorMessages.join(", ");
        });
        EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
        return false;
    } else {
        return true;
    }
}

loadDropdownItems();
</script>
